import { logger } from './logger-service'
import { ChildRepository, Child, ChildInsert } from '@/lib/repositories/child.repository'

export type { Child }

export class ChildService {
  public static async getChildrenByCaregiver(caregiverId: string): Promise<Child[]> {
    logger.debug('ChildService.getChildrenByCaregiver called', {
      component: 'ChildService',
      arguments: { caregiverId }
    });
    try {
      const children = await ChildRepository.getByCaregiver(caregiverId)
      
      // Sort children by name
      const sortedChildren = children.sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )

      logger.info('Successfully fetched children for caregiver', {
        caregiverId,
        childCount: children.length
      })

      return sortedChildren
    } catch (error) {
      logger.error('Failed to fetch children for caregiver', {
        error,
        caregiverId
      })
      throw error
    }
  }

  public static async addChild(child: ChildInsert): Promise<Child> {
    logger.debug('ChildService.addChild called', {
      component: 'ChildService',
      arguments: { child }
    });
    try {
      // Check if there's a soft-deleted child with the same name
      const deletedChild = await ChildRepository.getDeletedChildByNameAndCaregiver(
        child.name, 
        child.caregiver_id
      );
      
      // If found, reactivate the child instead of creating a new one
      if (deletedChild) {
        logger.info('Reactivating previously deleted child', {
          childId: deletedChild.id,
          childName: deletedChild.name,
          caregiverId: child.caregiver_id
        });
        
        return await ChildRepository.reactivateChild(deletedChild.id);
      }
      
      // Otherwise, create a new child
      const newChild = await ChildRepository.create({
        ...child,
        isdeleted: false
      });
      
      logger.info('Successfully added new child', {
        childId: newChild.id,
        caregiverId: newChild.caregiver_id
      })

      return newChild
    } catch (error) {
      logger.error('Failed to add child', {
        error,
        child
      })
      throw error
    }
  }

  public static async updateChild(childId: string, updates: Partial<Omit<Child, 'id' | 'created_at' | 'updated_at'>>): Promise<Child> {
    logger.debug('ChildService.updateChild called', {
      component: 'ChildService',
      arguments: { childId, updates }
    });
    try {
      const updatedChild = await ChildRepository.update(childId, updates)
      
      logger.info('Successfully updated child', {
        childId,
        updatedFields: Object.keys(updates)
      })

      return updatedChild
    } catch (error) {
      logger.error('Failed to update child', {
        error,
        childId,
        updates
      })
      throw error
    }
  }

  public static async softDeleteChild(childId: string): Promise<Child> {
    logger.debug('ChildService.softDeleteChild called', {
      component: 'ChildService',
      arguments: { childId }
    });
    
    try {
      const updatedChild = await ChildRepository.update(childId, { 
        isdeleted: true,
        updated_at: new Date().toISOString()
      });
      
      logger.info('Successfully soft deleted child', {
        childId
      });
      
      return updatedChild;
    } catch (error) {
      logger.error('Failed to soft delete child', {
        error,
        childId
      });
      throw error;
    }
  }

  public static async archiveChild(childId: string): Promise<void> {
    logger.debug('ChildService.archiveChild called', {
      component: 'ChildService',
      arguments: { childId }
    });
    
    try {
      await ChildRepository.update(childId, { 
        archived: true,
        updated_at: new Date().toISOString()
      });
      
      logger.info('Successfully archived child', {
        childId
      });
    } catch (error) {
      logger.error('Failed to archive child', {
        error,
        childId
      });
      throw error;
    }
  }

  public static async unarchiveChild(childId: string): Promise<void> {
    logger.debug('ChildService.unarchiveChild called', {
      component: 'ChildService',
      arguments: { childId }
    });
    
    try {
      await ChildRepository.update(childId, { 
        archived: false,
        updated_at: new Date().toISOString()
      });
      
      logger.info('Successfully unarchived child', {
        childId
      });
    } catch (error) {
      logger.error('Failed to unarchive child', {
        error,
        childId
      });
      throw error;
    }
  }
}
